import { useEffect, useState } from "react"

type BetterDocFetchSettledState<TRequest, TData> =
  | { request: TRequest; status: "loaded"; data: TData }
  | { request: TRequest; status: "failed" }

type BetterDocFetchState<TRequest, TData> =
  | { status: "idle" }
  | { status: "loading"; previousState: BetterDocFetchSettledState<TRequest, TData> | null }
  | BetterDocFetchSettledState<TRequest, TData>

const IDLE_STATE = { status: "idle" } as const

const LOADING_STATE = { status: "loading", previousState: null } as const

export const useBetterDocFetch = <TRequest, TData>(
  request: TRequest | null,
  fetchData: (request: TRequest) => Promise<TData>,
): BetterDocFetchState<TRequest, TData> => {
  const [settledState, setSettledState] = useState<BetterDocFetchSettledState<TRequest, TData> | null>(null)

  useEffect(() => {
    if (request === null) {
      return
    }

    const requestToFetch = request
    let isRequestStale = false

    fetchData(requestToFetch)
      .then((data) => {
        if (!isRequestStale) {
          setSettledState({ request: requestToFetch, status: "loaded", data })
        }
      })
      .catch(() => {
        if (!isRequestStale) {
          setSettledState({ request: requestToFetch, status: "failed" })
        }
      })

    return () => {
      isRequestStale = true
    }
  }, [request, fetchData])

  if (request === null) {
    return IDLE_STATE
  }

  if (settledState !== null && settledState.request === request) {
    return settledState
  }

  return settledState === null ? LOADING_STATE : { status: "loading", previousState: settledState }
}
